/**
 * FC-2 — офлайн-черга критичних дій швеї (IndexedDB).
 *
 * Проблема: при обриві Wi-Fi дія («ЗАВЕРШИТИ», «Взяти пачку») губилася майже
 * мовчки. Тепер такі POST-и ставляться в персистентну чергу й автоматично
 * синхронізуються, щойно з'явиться мережа.
 *
 * Безпека повторів: кожна дія несе `idempotency_key`. Сервер-дедуп (FC-5)
 * гарантує, що навіть якщо перша спроба таки долетіла (а відповідь загубилась),
 * повтор не задвоїть запис. До FC-5 залишковий ризик мінімальний (черга шле
 * дію рівно раз у нормальному офлайн-сценарії).
 */
import { apiClient } from "@/api/client";
import { toast } from "@/store/toast";
import { useAuthStore } from "@/store/auth";

const DB_NAME = "mes-offline";
const STORE = "queue";
// Версія 2 — спільна з `offlineReads`, і це НЕ дрібниця. `indexedDB.open` із
// НИЖЧОЮ версією, ніж уже створена, кидає VersionError: щойно кеш читань
// підняв би базу до 2, цей файл перестав би відкриватись — тобто зламалась
// би сама черга, яка береже гроші при обриві мережі. Тому версія тут і там
// однакова, а обидва обробники оновлення створюють ОБИДВА сховища.
const DB_VERSION = 2;

export interface QueuedAction {
  id: string;
  url: string;
  body: Record<string, unknown>;
  label: string;
  createdAt: number;
  idempotencyKey: string;
  /** Хто зробив (sub токена). Спільний телефон-термінал: дія швеї А не має
   *  піти з токеном швеї Б, що зайшла після автовиходу (код-ревю 05.11.2026). */
  owner?: string;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" });
      // Чуже сховище створюємо теж: оновлення може прийти з будь-якого боку,
      // і той, хто відкрив базу першим, мусить лишити її придатною для обох.
      if (!db.objectStoreNames.contains("reads")) db.createObjectStore("reads", { keyPath: "url" });
    };
    // `blocked` — коли інша вкладка ще тримає СТАРУ версію бази. Без цього
    // обробника `open` не викликає ні `onsuccess`, ні `onerror`: обіцянка
    // просто ніколи не виконується, і `await` висить вічно. `try/catch` зависання
    // не ловить. Наслідок був би найгіршим із можливих: `postCritical` завис би
    // на `enqueue`, дія не пішла б і не стала б у чергу, а `flushQueue` уже
    // виставив `flushing = true` — тобто синхронізація в цій вкладці зупинилась
    // би вся й мовчки. До підняття версії всі вкладки були v1 і `blocked` не
    // траплявся; тепер, у день викотки, це реальний випадок.
    req.onblocked = () => reject(new Error("offline-db: інша вкладка тримає стару версію"));
    req.onsuccess = () => {
      // Стара вкладка мусить відпустити базу, коли нова піднімає версію —
      // інакше вона й буде тим, хто блокує.
      req.result.onversionchange = () => req.result.close();
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onerror = () => reject(req.error);
    if (mode === "readonly") {
      req.onsuccess = () => resolve(req.result);
      return;
    }
    // ЗАПИС підтверджуємо лише КОМІТОМ транзакції, а не успіхом запиту.
    // `req.onsuccess` спрацьовує РАНІШЕ за коміт, і транзакція ще може
    // впасти — класика тут `QuotaExceededError`. Тоді `enqueue` рапортував
    // би успіх, людина бачила б «крій у черзі», а в базі не було б нічого:
    // рівно та сама тиша, що коштувала крою на Т0000000168, лише іншим
    // шляхом.
    let result: T;
    req.onsuccess = () => { result = req.result; };
    t.oncomplete = () => resolve(result);
    t.onabort = () => reject(t.error ?? new Error("offline-db: транзакцію скасовано"));
    t.onerror = () => reject(t.error);
  });
}

async function putAction(a: QueuedAction): Promise<void> {
  await tx("readwrite", (s) => s.put(a));
}
async function allActions(): Promise<QueuedAction[]> {
  let stored: QueuedAction[] = [];
  try {
    stored = (await tx<QueuedAction[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedAction[]>)) ?? [];
  } catch {
    // Сховище недоступне — працюємо з тим, що є в памʼяті.
  }
  return [...stored, ...memoryQueue].sort((a, b) => a.createdAt - b.createdAt);
}
async function delAction(id: string): Promise<void> {
  const i = memoryQueue.findIndex((a) => a.id === id);
  if (i >= 0) memoryQueue.splice(i, 1);
  try {
    await tx("readwrite", (s) => s.delete(id));
  } catch {
    // Якщо рядок був лише в памʼяті — його вже прибрано вище.
  }
}
async function countActions(): Promise<number> {
  let stored = 0;
  try {
    stored = await tx<number>("readonly", (s) => s.count());
  } catch {
    stored = 0;
  }
  return stored + memoryQueue.length;
}

// ---- підписка на к-сть у черзі (для банера) ----
type Listener = (count: number) => void;
const listeners = new Set<Listener>();

export function subscribeQueue(fn: Listener): () => void {
  listeners.add(fn);
  countActions().then(fn).catch(() => {});
  return () => listeners.delete(fn);
}
async function emit(): Promise<void> {
  const c = await countActions();
  listeners.forEach((l) => l(c));
}

// ---- публічне API ----

export async function enqueue(url: string, body: Record<string, unknown>, label: string): Promise<QueuedAction> {
  // Ключ БЕРЕМО з тіла, якщо він там уже є, і мінтимо новий лише коли його
  // немає зовсім.
  //
  // Чому це не дрібниця. `postCritical` кладе ключ у тіло ЩЕ ДО спроби
  // надіслати — і саме та спроба могла долетіти до сервера, поки відповідь
  // губилась у тунелі. Новий ключ означає, що повтор із черги виглядає для
  // сервера як НОВА дія: `db_idempotency.claim` пропускає його, і запис
  // створюється вдруге. Для дій, що створюють кількість (взяти пачку,
  // оформити роботу за швею), це подвійна ВІДРЯДНА ОПЛАТА — рівно те, від
  // чого ця черга й мала берегти, і що обіцяє шапка модуля.
  const carried = body["idempotency_key"];
  const key = typeof carried === "string" && carried ? carried : uuid();
  const owner = useAuthStore.getState().claims?.sub ?? "";
  const a: QueuedAction = { id: uuid(), url, body, label, createdAt: Date.now(), idempotencyKey: key, owner };
  try {
    await putAction(a);
  } catch {
    // Сховище недоступне — але дію людина вже зробила, і втратити її не можна.
    memoryQueue.push(a);
  }
  await emit();
  return a;
}

/** Чи лежить бодай щось у ПАМʼЯТІ (тобто переживе лише цю вкладку). */
export function hasVolatileQueue(): boolean {
  return memoryQueue.length > 0;
}

export interface CriticalResult<T> {
  queued: boolean;
  data?: T;
}

/**
 * Критичний POST, стійкий до офлайну. Якщо мережі немає (або запит не долетів) —
 * дія персиститься й піде автоматично пізніше. Клієнтські 4xx — це справжня
 * помилка валідації, її кидаємо назад (у чергу не ставимо).
 *
 * ❓ `postCritical` vs `useIdemKey` (`lib/idem.ts`) — обидва проти подвійного
 * запису, але для РІЗНОЇ форми екрана (06.10.2026):
 *
 * - **`postCritical`** — тап-і-забув у цеху (ВТО, швейка, крій): ключ новий
 *   щоразу, офлайн ставить дію в чергу IndexedDB і шле сама, коли мережа
 *   повернеться. Людина не бачить форми з «Зберегти» — лише кнопку дії.
 * - **`useIdemKey`** — форма з явним «Зберегти»/«Записати» (каса, ОТК,
 *   технолог): ключ живе, ПОКИ людина не натисне кнопку ще раз або поки
 *   форма не закриється, і розрізняє «сервер точно не записав» (код
 *   відповіді є — ключ лишається, безпечно повторити) від «відповіді
 *   не було» (мережа обірвалась — ключ міняється, бо сервер МІГ встигнути
 *   записати). Офлайн не чергує — запит просто падає з помилкою, людина
 *   тисне ще раз, коли мережа повернеться.
 *
 * Новий екран з грошима/кількістю: один тап без форми → `postCritical`;
 * форма з полями й кнопкою — `useIdemKey`.
 */
export async function postCritical<T = unknown>(
  url: string,
  body: Record<string, unknown>,
  label: string,
): Promise<CriticalResult<T>> {
  const key = uuid();
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    await enqueue(url, { ...body, idempotency_key: key }, label);
    return { queued: true };
  }
  try {
    const r = await apiClient.post<{ data: T }>(url, { ...body, idempotency_key: key });
    return { queued: false, data: r.data.data };
  } catch (e) {
    const err = e as { response?: { status?: number } };
    // Немає response = мережевий збій → у чергу (не втрачаємо дію).
    if (!err.response) {
      await enqueue(url, { ...body, idempotency_key: key }, label);
      return { queued: true };
    }
    throw e; // 4xx/5xx з відповіддю — справжня помилка
  }
}

/**
 * Запасна черга В ПАМʼЯТІ.
 *
 * Куплено 11.09.2026: закрійник заніс крій, натиснув «Записати» — і не сталось
 * НІЧОГО. Ні запиту на сервер, ні повідомлення. На сервері — жодного сліду.
 *
 * Причина в тому, що `enqueue` міг ВІДМОВИТИ: `indexedDB.open` не відкриває
 * базу, поки інша вкладка тримає стару версію (а планшети в цеху не закривають
 * вкладок тижнями). Виняток летів у `postCritical`, звідти в екран крою, де на
 * нього чекав порожній `catch` — і робота зникала беззвучно.
 *
 * Тепер сховище — це ЗРУЧНІСТЬ, а не умова. Не змогли записати на диск —
 * тримаємо в памʼяті й пробуємо надіслати далі. Памʼять не переживе
 * перезавантаження, тому людині про це кажуть прямо (див. `postCritical`).
 */
const memoryQueue: QueuedAction[] = [];

let flushing = false;

export async function flushQueue(): Promise<void> {
  if (flushing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushing = true;
  try {
    const items = await allActions();
    const me = useAuthStore.getState().claims?.sub ?? "";
    for (const a of items) {
      // Чужа дія чекає свого автора (він увійде — і вона піде з ЙОГО токеном).
      if (a.owner && a.owner !== me) continue;
      try {
        await apiClient.post(a.url, { ...a.body, idempotency_key: a.idempotencyKey });
        await delAction(a.id);
      } catch (e) {
        const st = (e as { response?: { status?: number } }).response?.status;
        // 401/403 — НЕ помилка даних. Токен міг протермінуватись, зміну могли
        // закрити: повтор після входу спрацює. Доти ми викидали такі дії з
        // черги назавжди — тобто стирали вже зроблену роботу через те, що
        // планшет полежав. 429 — теж «спробуй пізніше».
        if (st === 401 || st === 403 || st === 429) {
          break;
        }
        if (st && st >= 400 && st < 500) {
          // Справжня помилка даних — повтор не допоможе. Прибираємо, але
          // кажемо це ГОЛОСНО: людина мусить знати, що її запис не пройшов.
          await delAction(a.id);
          toast.error(
            `НЕ записано: ${a.label}. Сервер відхилив дані — повторіть уручну.`,
            0,
          );
        } else {
          break; // мережа/5xx — зупиняємось, спробуємо пізніше
        }
      }
    }
  } finally {
    flushing = false;
    await emit();
  }
}

// ---- авто-синхронізація ----
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    toast.success("Мережа відновлена — синхронізую збережені дії…");
    void flushQueue();
  });
  // періодична спроба (на випадок «тихого» повернення без події online)
  window.setInterval(() => void flushQueue(), 15_000);
  // при старті — доштовхнути залишки
  void flushQueue();
}
